"use client";

import css from "./header.module.scss";
import HomeNavigationItemWithSubnavigation, { HomeNavigationItemWithSubnavigationRef } from "@/components/home/home-navigation-item-with-subnavigation/home-navigation-item-with-subnavigation";
import Link, { LinkType } from "../link/link";
import { useRef } from "react";
import BaseLink from "../base-link/base-link";
import GridColumn from "../grid-column/grid-column";
import GridRow from "../grid-row/grid-row";
import HeaderLinks from "./header-links/header-links";
import HeaderMenu from "./header-menu/header-menu";
import Image from "../image/image";
import { Navigation, Page, SiteSettings } from "@/sanity/sanity.types";
import { useLayout } from "@/contexts/layout-context";
import { usePathname } from "next/navigation";
import { useAnalytics } from "@/contexts/analytics-context";
export interface IHeaderProps {
  data: Navigation;
  siteSettings: SiteSettings;
  abTestDataAS147?: Page;
}
const Header = (props: IHeaderProps) => {
  const {
    siteSettings,
    data,
    abTestDataAS147
  } = props;
  const {
    items,
    primaryCta
  } = data || {};
  const {
    trustBar
  } = siteSettings || {};
  const {
    setNavOpen
  } = useLayout();
  const submenuRef = useRef<HomeNavigationItemWithSubnavigationRef>({
    setExpanded: () => {}
  });
  const onSubmenuOpen = (ref: HomeNavigationItemWithSubnavigationRef) => {
    if (submenuRef.current && submenuRef.current !== ref) {
      submenuRef.current.setExpanded(false);
    }
    submenuRef.current = ref;
    setNavOpen(true);
  };
  const onSubmenuClose = (ref_0: HomeNavigationItemWithSubnavigationRef) => {
    if (submenuRef.current === ref_0) {
      setNavOpen(false);
    }
  };

  //--- A/B Test AS147 Start----------
  const pathname = usePathname();
  const getAS147Info = () => {
    try {
      const {
        optimizelyDecisionsRan,
        getOptimizelyDecisionInfo
      } = useAnalytics();
      const isHomePage = pathname === "/";
      const renderContentAS147 = isHomePage && optimizelyDecisionsRan || !isHomePage;
      const variationKeyAS147 = getOptimizelyDecisionInfo("AS147");
      const shouldRunAS147 = optimizelyDecisionsRan && variationKeyAS147 && variationKeyAS147 !== "control";
      const modules = abTestDataAS147?.modules || [];
      const heroOptionsTest147Modules = modules.filter(module => module._type === "heroOptions");
      const variantContentMap: Record<string, number> = {
        v1: 0,
        v2: 1,
        v3: 2
      };
      const selectedIndex = variantContentMap[variationKeyAS147 as string];
      const selectedHeroAS147 = heroOptionsTest147Modules?.[selectedIndex];
      return {
        shouldRunAS147,
        variationKeyAS147,
        renderContentAS147,
        selectedHeroAS147
      };
    } catch {
      return {};
    }
  };
  const {
    shouldRunAS147: shouldRunAS147_0,
    variationKeyAS147: variationKeyAS147_0,
    renderContentAS147: renderContentAS147_0,
    selectedHeroAS147: selectedHeroAS147_0
  } = getAS147Info();
  // --- A/B Test AS147 End----------

  const getCurrentMonth = () => {
    return new Date().toLocaleString("default", {
      month: "long"
    });
  };
  const currentMonth = getCurrentMonth();
  return <header id="header" className={`${css.header} sticky top-0 z-[1000] bg-[#FFF] text-[#373B37]`}>
      {/* Mobile */}
      <div className={`${css.headerMobile} !flex xl:!hidden`}>
        {shouldRunAS147_0 && <GridRow className={`bg-[#0E5E4F] text-white`}>
            <GridColumn>
              <div className="flex items-center justify-between gap-[12px] py-4 md:justify-start">
                <div className="flex items-center gap-[12px]">
                  <div className="flex h-[30px] flex-none items-center whitespace-nowrap rounded-[4px] bg-[#BC7100] px-2 text-xs font-medium uppercase">
                    {selectedHeroAS147_0?.eyebrow}
                  </div>

                  <div className="text-sm font-medium leading-tight md:text-base md:font-semibold md:leading-5">
                    <span className="text-balance md:text-nowrap">
                      {selectedHeroAS147_0?.headline}
                      {variationKeyAS147_0 === "v3" && ` ${currentMonth}`}
                    </span>
                  </div>
                </div>

                {shouldRunAS147_0 && <BaseLink href={selectedHeroAS147_0?.secondaryCta?.link?.href ?? ""} className="whitespace-nowrap rounded-md border-[1px] border-white px-2 text-sm font-medium md:text-base md:font-semibold md:leading-[26px]">
                    {selectedHeroAS147_0?.secondaryCta?.link?.label}
                  </BaseLink>}
              </div>
            </GridColumn>
          </GridRow>}
      </div>
      <div className={`${css.headerMobile} !flex xl:!hidden`}>
        <GridRow>
          <GridColumn>
            <div className={`${css.headerMobileContent} h-[72px]`}>
              <BaseLink href="/" data-cta-id="logo" data-cta-value="logo-header">
                <Image className={css.headerMobileLogo} src="/logo.svg" alt="Amerisave Logo" width={160} height={40} fetchPriority="high" />
              </BaseLink>
              {items && <HeaderMenu data={items} primaryCta={primaryCta} className="md:hidden" trustBar={trustBar} />}
              <div className="hidden items-center gap-8 md:flex">
                {primaryCta?.link && <Link href={primaryCta.link.href ?? "#"} linkType={LinkType.BUTTON} data-cta-id={primaryCta?.link?.ctaId ?? ""} data-cta-value={primaryCta?.link?.ctaValue ?? ""}>
                    {primaryCta.link.label}
                  </Link>}
                {items && <HeaderMenu data={items} />}
              </div>
            </div>
          </GridColumn>
        </GridRow>
      </div>
      {/* Desktop */}
      <div className={`${css.headerDesktop} !hidden xl:!flex`}>
        {!renderContentAS147_0 && <div />}

        {shouldRunAS147_0 && <div className={`${css.headerDesktopTop} !bg-[#0E5E4F]`}>
            <GridRow className="!max-w-[1488px]">
              <GridColumn>
                <div className={css.headerDesktopTopContent}>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex h-[28px] items-center whitespace-nowrap rounded-[4px] bg-[#BC7100] px-2 font-medium uppercase">
                      {selectedHeroAS147_0?.eyebrow}
                    </div>

                    <div className="text-base font-semibold leading-5 tracking-[-0.16px]">
                      {selectedHeroAS147_0?.headline}
                      {variationKeyAS147_0 === "v3" && ` ${currentMonth}`}
                    </div>

                    {shouldRunAS147_0 && <BaseLink href={selectedHeroAS147_0?.secondaryCta?.link?.href ?? ""} className="whitespace-nowrap rounded-md border-[1px] border-white px-2 text-base font-semibold leading-[26px]">
                        {selectedHeroAS147_0?.secondaryCta?.link?.label}
                      </BaseLink>}
                  </div>
                </div>
              </GridColumn>
            </GridRow>
          </div>}

        <div className={css.headerDesktopBottom}>
          <GridRow className="!max-w-[1488px]">
            <GridColumn>
              <div className={css.headerDesktopBottomContent}>
                <div className="flex items-center justify-center gap-6">
                  <BaseLink href="/" data-cta-id={"logo"} data-cta-value={"logo-header"} className="relative">
                    <Image className={`relative ${css.headerDesktopLogo} top-[-5px]`} src="/logo.svg" alt="Amerisave Logo" width={160} height={40} fetchPriority="high" />
                  </BaseLink>
                  {!!items?.length && <nav className={css.headerDesktopNav}>
                      <ul className={`${css.headerDesktopNavList} relative`}>
                        {items.filter(item => item.link).map((item_0, index) => <li key={index}>
                              {!!item_0.children?.length ? <HomeNavigationItemWithSubnavigation data={item_0} onOpen={onSubmenuOpen} onClose={onSubmenuClose} /> : <BaseLink className={css.headerDesktopNavListItemLink} href={item_0.link!.href ?? ""} data-cta-id={item_0?.link?.ctaId ?? ""} data-cta-value={item_0?.link?.ctaValue ?? ""} rel={item_0?.link?.nofollow}>
                                  {item_0.link!.label}
                                </BaseLink>}
                            </li>)}
                      </ul>
                    </nav>}
                </div>
                <div className="flex items-center justify-center gap-6">
                  {trustBar && <div className={css.headerLinksWrapper}>
                      <HeaderLinks data={trustBar} className={css.stickyHeader} />
                    </div>}
                  {primaryCta?.link && <Link href={primaryCta.link.href ?? "#"} linkType={LinkType.BUTTON} className="whitespace-nowrap" data-cta-id={primaryCta.link?.ctaId ?? ""} data-cta-value={primaryCta.link?.ctaValue ?? ""} rel={primaryCta?.link?.nofollow}>
                      {primaryCta.link.label}
                    </Link>}
                </div>
              </div>
            </GridColumn>
          </GridRow>
        </div>
      </div>
    </header>;
};
export default Header;